import { motion } from 'framer-motion';
import { CheckCircle, Warning, Lightbulb, TrendUp } from '@phosphor-icons/react';

export default function SEOAnalysis({ analysis, suggestions }) {
  if (!analysis || !suggestions) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={18} weight="fill" className="text-green-500" />;
      case 'warning':
        return <Warning size={18} weight="fill" className="text-yellow-500" />;
      case 'info':
        return <Lightbulb size={18} weight="fill" className="text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="space-y-4"
    >
      {/* Score SEO */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendUp size={24} weight="duotone" className="text-primary" />
            Analyse SEO
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Score:</span>
            <span className={`text-3xl font-bold ${getScoreColor(analysis.seo_score)}`}>
              {analysis.seo_score}
            </span>
            <span className="text-muted-foreground">/100</span>
          </div>
        </div>

        {/* Mots-clés principaux */}
        {analysis.keywords && analysis.keywords.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Mots-clés principaux:</p>
            <div className="flex flex-wrap gap-2">
              {analysis.keywords.map((kw, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                >
                  {kw.word}
                  <span className="text-xs opacity-70">×{kw.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Indicateurs */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            {analysis.has_location ? (
              <CheckCircle size={16} weight="fill" className="text-green-500" />
            ) : (
              <Warning size={16} weight="fill" className="text-yellow-500" />
            )}
            <span>Localisation</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {analysis.has_activity ? (
              <CheckCircle size={16} weight="fill" className="text-green-500" />
            ) : (
              <Warning size={16} weight="fill" className="text-yellow-500" />
            )}
            <span>Activité</span>
          </div>
        </div>
      </div>

      {/* Suggestions SEO */}
      {suggestions && suggestions.length > 0 && (
        <div className="glass rounded-xl p-6">
          <h4 className="text-sm font-semibold mb-4">Suggestions SEO</h4>
          <div className="space-y-3">
            {suggestions.map((suggestion, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-lg bg-background/50"
              >
                {getIconForType(suggestion.type)}
                <span className="text-sm flex-1">{suggestion.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
